import { useEffect, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProposalExtraItemType } from '@econtractor/types';
import { ServicesSelect } from 'modules/product';
import InputQuantity from 'components/others/InputQuantity';
import spinnerTitle from 'components/others/spinnerTitle';
import AdditionalItemsTable from './AdditionalItemsTable';
import useTemplateAdditionalItems from './model/useTemplateAdditionalItems';
import useUpdateAdditionalPrices from './model/useUpdateAdditionalPrices';
import useSettingsParams from 'hooks/useSettingsParams';
import SolarIcon from 'components/icons/SolarIcon';

export interface TemplateAdditionalItemsProps {
    items: ProposalExtraItemType[];
    rate: number;
    localeNs?: string;
    disabled?: boolean;
    onChange: (items: ProposalExtraItemType[]) => void;
}

const TemplateAdditionalItems = ({
    items,
    rate,
    localeNs = 'Proposal',
    disabled = false,
    onChange,
}: TemplateAdditionalItemsProps) => {
    const { t } = useTranslation([localeNs, 'common']);
    const params = useSettingsParams();
    const showAsOtherExpense = localeNs === 'Proposal' && Boolean(params.includeOtherInObjectCost);
    const quantityRef = useRef<HTMLInputElement>(null);
    const {
        sourceType,
        handleSourceChange,
        selectedService,
        setSelectedService,
        draftName,
        setDraftName,
        draftCount,
        setDraftCountDown,
        setDraftCountUp,
        setDraftCountFromInput,
        draftPrice,
        setDraftPriceFromInput,
        canAdd,
        handleAdd,
        handleDraftKeyDown,
    } = useTemplateAdditionalItems({ items, rate, onChange });

    const { isUpdating, canUpdate, handleUpdatePrices } = useUpdateAdditionalPrices({
        items,
        rate,
        localeNs,
        onChange,
    });

    const showActions = sourceType === 'manual' || !!selectedService?.value;

    useEffect(() => {
        if (sourceType !== 'catalog' || !selectedService?.value) return;
        quantityRef.current?.focus();
    }, [sourceType, selectedService?.value]);

    return (
        <>
            {!disabled ? (
                <div className="border rounded p-3 mb-3">
                    <div className="btn-group w-100 mb-3">
                        <Button
                            size="sm"
                            variant={sourceType === 'catalog' ? 'primary' : 'outline-primary'}
                            onClick={() => handleSourceChange('catalog')}
                        >
                            <SolarIcon name="briefcase" className="me-1" />
                            {t('edit.additional.modeCatalog')}
                        </Button>
                        <Button
                            size="sm"
                            variant={sourceType === 'manual' ? 'primary' : 'outline-primary'}
                            onClick={() => handleSourceChange('manual')}
                        >
                            <SolarIcon name="pencil-square" className="me-1" />
                            {t('edit.additional.modeManual')}
                        </Button>
                    </div>

                    {sourceType === 'catalog' ? (
                        <ServicesSelect
                            title={t('edit.additional.fields.service')}
                            required={false}
                            value={selectedService?.value || null}
                            onChange={(opt) => setSelectedService(opt)} />
                    ) : (
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted mb-1 fs-12">{t('edit.additional.fields.itemName')}</Form.Label>
                            <Form.Control
                                value={draftName}
                                onChange={(e) => setDraftName(e.target.value)}
                                placeholder={t('edit.additional.placeholders.itemName')}
                                onKeyDown={handleDraftKeyDown}
                            />
                        </Form.Group>
                    )}

                    {showActions ? (
                        <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-end">
                            <InputQuantity
                                ref={quantityRef}
                                label={t('edit.additional.fields.itemCount')}
                                value={draftCount}
                                integerOnly
                                onDown={setDraftCountDown}
                                onUp={setDraftCountUp}
                                onChange={setDraftCountFromInput}
                                onEnter={handleAdd}
                            />

                            {sourceType === 'manual' ? (
                                <Form.Group className="mb-0">
                                    <Form.Label className="text-muted mb-1 fs-12">{t('edit.additional.fields.itemPrice')}</Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        inputMode="decimal"
                                        value={draftPrice}
                                        onChange={(e) => setDraftPriceFromInput(e.target.value)}
                                        onKeyDown={handleDraftKeyDown}
                                    />
                                </Form.Group>
                            ) : null}

                            <Button type="button" variant="primary" size="sm" className="mb-0" disabled={!canAdd} onClick={handleAdd}>
                                <SolarIcon name="plus" className="me-1" />
                                {t('edit.additional.add')}
                            </Button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {items.length ? (
                <>
                    {!disabled ? (
                        <div className="d-flex justify-content-end mb-2">
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                disabled={!canUpdate || isUpdating}
                                onClick={() => void handleUpdatePrices()}
                            >
                                {spinnerTitle(
                                    <>
                                        <SolarIcon name="arrow-repeat" className="me-1" />
                                        {t('edit.additional.updatePrices')}
                                    </>,
                                    isUpdating,
                                    t('edit.additional.updatePrices'),
                                )}
                            </button>
                        </div>
                    ) : null}
                    <AdditionalItemsTable
                        items={items}
                        rate={rate}
                        localeNs={localeNs}
                        disabled={disabled}
                        showAsOtherExpense={showAsOtherExpense}
                        onChange={onChange}
                    />
                </>
            ) : (
                <p className="text-muted mb-0">{t('edit.additional.empty')}</p>
            )}
        </>
    );
};

export default TemplateAdditionalItems;
